import { useEffect, useState } from "react";
import {
  getGlobalSummaryUrl,
  getGlobalSummarySnapshotUrl,
} from "./constants/healthEndpoints";

/**
 * The global roll-up, aggregated across every live market.
 *
 * Two sources, raced deliberately rather than tried in sequence:
 *
 *   snapshot  a static JSON object on S3, refreshed on a schedule. No Lambda in
 *             the request path, so it is edge-cached and lands in tens of
 *             milliseconds — and it still answers when a market API or the
 *             proxy is down.
 *   live      the proxy fans out to the markets and aggregates on demand,
 *             ~800ms.
 *
 * Both start at once. Whichever arrives first renders; the live result then
 * replaces the snapshot if it is actually newer. The comparison matters — the
 * snapshot can win the race *and* be newer than a live response that was slow
 * enough to be queued behind a cold start, and flip-flopping the numbers is
 * worse than being a minute stale.
 *
 * There is no fallback to placeholder figures. A wrong number on the front page
 * is worse than a visibly missing one, because nobody goes looking for the bug
 * behind a plausible total.
 */
export default function useGlobalSummary() {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
    source: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    let settledFrom = null;
    let newestAt = 0;

    const accept = (data, source) => {
      const at = Number(data?.generatedAt) || 0;
      // Never move backwards in time, whichever source arrives second.
      if (settledFrom && at <= newestAt) return;
      newestAt = at;
      settledFrom = source;
      setState({ data, loading: false, error: null, source });
    };

    const load = async (url, source) => {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      accept(await response.json(), source);
    };

    const attempts = [
      load(getGlobalSummarySnapshotUrl(), "snapshot"),
      load(getGlobalSummaryUrl(), "live"),
    ].map((p) => p.catch((error) => error));

    Promise.all(attempts).then((results) => {
      if (controller.signal.aborted || settledFrom) return;
      // Both failed: surface the live endpoint's reason, which is the more
      // informative of the two.
      const error = results[1] instanceof Error ? results[1] : results[0];
      setState({ data: null, loading: false, error, source: null });
    });

    return () => controller.abort();
  }, []);

  return state;
}
